import { computePlanProgress, type PlanProgressItem } from '@pfo/domain';
import { currentPeriod } from '@pfo/shared';

import { formatVnd } from '../../../shared/lib/money.js';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { useInvestmentOverview } from '../hooks/useInvestmentOverview.js';
import type { PlanAllocationRow } from '../data/investmentRepository.js';
import styles from './InvestmentPage.module.css';

/**
 * Đầu tư — reading the plan, not making one.
 *
 * Plans are written by talking to the agent, never in the app (decision X1). This screen is
 * the other half of that arrangement: the plan lives in the database, and this is where the
 * household looks at it and asks "how far along are we".
 *
 * It refuses to answer one question on purpose. Progress in MONEY is real — the ledger knows
 * what left for an investment destination. Progress in WEIGHT ("FUEVN100 is at 18%") needs
 * holdings, which arrive in Phase 3; until then the screen says so rather than showing a
 * number nobody can stand behind.
 */

const STANCE_LABELS: Record<string, string> = {
  buy: 'BUY',
  hold: 'HOLD',
  stop_buy: 'STOP BUY',
  reduce: 'REDUCE',
  sell: 'SELL',
};

const TARGET_LABELS: Record<string, string> = {
  capital_invested: 'Vốn giải ngân',
  dividend_income: 'Cổ tức nhận được',
  asset_weight: 'Tỷ trọng tài sản',
  sector_weight: 'Tỷ trọng ngành',
  minimum_cash_buffer: 'Mức sàn tiền mặt',
  maximum_single_stock_weight: 'Trần tỷ trọng một mã',
  fundamental_metric: 'Chỉ số cơ bản',
};

const REASON_LABELS: Record<NonNullable<PlanProgressItem['reason']>, string> = {
  needs_holdings: 'Cần dữ liệu nắm giữ từng mã (Phase 3)',
  not_tracked: 'Hệ chưa theo dõi số thực tế cho chỉ tiêu này',
};

function bandOf(row: PlanAllocationRow): string {
  if (row.minWeight !== null && row.maxWeight !== null) return `${row.minWeight}–${row.maxWeight}%`;
  if (row.targetWeight !== null) return `${row.targetWeight}%`;
  if (row.minWeight !== null) return `≥${row.minWeight}%`;
  if (row.maxWeight !== null) return `≤${row.maxWeight}%`;
  return '—';
}

function labelOf(row: PlanAllocationRow): string {
  return (
    row.symbol ?? row.assetClass ?? (row.sector === null ? '(không rõ)' : `Ngành ${row.sector}`)
  );
}

export function InvestmentPage() {
  const contextState = useCurrentContext();
  const subjectId = contextState.status === 'ready' ? contextState.context.selfSubject.id : null;
  const timeZone =
    contextState.status === 'ready' ? contextState.context.household.timezone : 'UTC';
  const year = currentPeriod(timeZone).year;
  const state = useInvestmentOverview(subjectId, year);

  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (state.status === 'loading' || state.status === 'idle')
    return <LoadingState label="Đang tải kế hoạch đầu tư…" />;
  if (state.status === 'error') {
    return <ErrorState message={state.message} onRetry={state.retry} />;
  }

  const { plan, allocations, targets, actual } = state.data;
  const progress = computePlanProgress({
    targets: targets.map((target) => ({
      targetType: target.targetType,
      targetValue: Number(target.targetValue),
      targetUnit: target.targetUnit,
      operator: target.operator,
      symbol: target.symbol,
    })),
    investedYtd: actual.investedYtd,
  });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Đầu tư {year}</h1>

      {plan === null ? (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>Chưa có kế hoạch nào đang hiệu lực</h2>
          <p className={styles.muted}>
            Kế hoạch đầu tư không được tạo trong app — bạn lập nó bằng cách trao đổi với AI Agent,
            agent ghi kế hoạch vào knowledge repo rồi cập nhật số vào hệ thống. Màn hình này là nơi
            xem lại và theo dõi tiến độ.
          </p>
        </section>
      ) : (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>{plan.name}</h2>
          {plan.version === null ? (
            <p className={styles.muted}>Kế hoạch chưa có phiên bản nào được kích hoạt.</p>
          ) : (
            <>
              <p className={styles.muted}>
                Bản V{plan.version.versionNumber} · hiệu lực từ {plan.version.effectiveFrom}
                {plan.version.effectiveTo === null
                  ? ' đến nay'
                  : ` đến ${plan.version.effectiveTo}`}
              </p>
              {plan.version.changeSummary === null ? null : <p>{plan.version.changeSummary}</p>}
              {plan.version.markdownPath === null ? null : (
                <p className={styles.muted}>
                  Chi tiết: {plan.version.markdownPath}
                  {plan.version.gitCommitSha === null
                    ? ''
                    : ` @ ${plan.version.gitCommitSha.slice(0, 7)}`}
                </p>
              )}
            </>
          )}
        </section>
      )}

      <section className={lists.card} aria-labelledby="progress-heading">
        <h2 className={lists.cardTitle} id="progress-heading">
          Tiến độ theo kế hoạch
        </h2>

        {progress.length === 0 ? (
          <p className={styles.muted}>Kế hoạch chưa đặt chỉ tiêu nào.</p>
        ) : (
          <ul className={lists.list}>
            {progress.map((item) => (
              <li key={`${item.targetType}-${item.symbol ?? ''}`}>
                <p className={styles.muted}>
                  {TARGET_LABELS[item.targetType] ?? item.targetType}
                  {item.symbol === null ? '' : ` (${item.symbol})`} · mục tiêu {item.operator}{' '}
                  {item.targetUnit === 'VND'
                    ? formatVnd(item.targetValue)
                    : `${item.targetValue}${item.targetUnit === 'percent' ? '%' : ''}`}
                </p>
                {item.comparable && item.actual !== null ? (
                  <>
                    <p className={styles.headline}>
                      {formatVnd(item.actual)}
                      {item.pct === null ? '' : ` — ${Math.round(item.pct)}%`}
                    </p>
                    <div
                      className={styles.bar}
                      role="progressbar"
                      aria-valuenow={item.pct === null ? 0 : Math.round(item.pct)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className={styles.barFill}
                        style={{ width: `${Math.min(100, Math.max(0, item.pct ?? 0))}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <p className={styles.muted}>
                    Chưa đối chiếu được — {REASON_LABELS[item.reason ?? 'not_tracked']}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={lists.card} aria-labelledby="allocation-heading">
        <h2 className={lists.cardTitle} id="allocation-heading">
          Phân bổ mục tiêu
        </h2>
        {allocations.length === 0 ? (
          <p className={styles.muted}>Bản kế hoạch này chưa khai phân bổ.</p>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Đối tượng</th>
                    <th scope="col">Band</th>
                    <th scope="col">Trạng thái</th>
                    <th className={styles.amount} scope="col">
                      Mỗi tháng
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((row) => (
                    <tr key={`${row.symbol ?? row.assetClass ?? row.sector}-${row.priority}`}>
                      <td>{labelOf(row)}</td>
                      <td>{bandOf(row)}</td>
                      <td className={styles.stance}>
                        {row.stance === null ? '—' : (STANCE_LABELS[row.stance] ?? row.stance)}
                      </td>
                      <td className={styles.amount}>
                        {row.monthlyTargetAmount === null
                          ? '—'
                          : formatVnd(Number(row.monthlyTargetAmount))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.notice}>
              Các band cố ý không cộng thành 100% — band nhóm và band từng mã là hai tầng khác nhau.{' '}
              <strong>Tỷ trọng thực tế chưa hiện được</strong>: hệ chưa lưu số lượng nắm giữ từng
              mã, phần đó thuộc giai đoạn sau.
            </p>
          </>
        )}
      </section>

      <section className={lists.card} aria-labelledby="actual-heading">
        <h2 className={lists.cardTitle} id="actual-heading">
          Thực tế theo sổ
        </h2>
        <ul className={lists.list}>
          <li className={lists.row}>
            <span>Đã giải ngân trong năm</span>
            <strong>{formatVnd(actual.investedYtd)}</strong>
          </li>
          <li className={lists.row}>
            <span>Số dư tài khoản chứng khoán</span>
            <strong>{formatVnd(actual.brokerBalance)}</strong>
          </li>
          {actual.brokerAccounts.map((account) => (
            <li className={lists.row} key={account.id}>
              <span className={styles.muted}>{account.name}</span>
              <span className={styles.muted}>{formatVnd(Number(account.balance))}</span>
            </li>
          ))}
        </ul>
        <p className={styles.muted}>
          Số dư là tiền đã ghi sổ, không phải giá trị thị trường của danh mục.
        </p>
      </section>
    </div>
  );
}
